import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getCarguideResults as getCarguideResultsCall,
  saveMemberAnswers,
  GetCarGuideResultsResponse
} from '~/helpers/orchestration/carguide';
import Wrapper from './components/Wrapper';
import {
  localForageSetItem,
  localForageGetItem,
  localForageRemoveItem
} from '~/helpers/client/localForage';
import { renderRoutes } from '~/config/routes/renderRoutes';
import { idToken as idTokenSelector } from '~/App/shared/selectors/sessionSelector';
import {
  localForageErrorHandler,
  requestErrorHandler
} from '~/helpers/notifyError';
import { TranslateFunction, useTranslation } from '~/Locale';
import {
  CarGuideAnswers,
  CarGuideIsValidSavedAnswers,
  CarGuideResults,
  CarguideQuestionType
} from '~/App/views/Carguide/types';
import StatusPageLoading from '~/App/shared/components/StatusPageLoading';
import { useCarguideCmsData } from './hooks';
import { RouteComponentProps } from '~/App/shared/interfaces/routes';
import KvdHelperWrapper from '~/App/shared/components/KvdHelperWrapper';

export const CarGuideResultsLimit = 8;

const isValidSavedAnswers = ({
  savedAnswerKeys,
  currentQuestions = []
}: CarGuideIsValidSavedAnswers): boolean => {
  if (savedAnswerKeys.length === 0) {
    return false;
  }

  const lessThanOrEqualAmount =
    savedAnswerKeys.length <= currentQuestions.length;
  const isAnsweredQuestionsOrderCorrect = savedAnswerKeys.every(
    (answer, index) => currentQuestions?.[index]?.tag === answer
  );

  return lessThanOrEqualAmount && isAnsweredQuestionsOrderCorrect;
};

const initialRequestHandler = {
  isLoading: false,
  isFetched: false,
  isError: false
};

const getSteps = (carguideUrl: string, t: TranslateFunction) => ({
  start: `/${carguideUrl}/${encodeURIComponent(t('ROUTES.CARGUIDE_START'))}`,
  flow: `/${carguideUrl}/${encodeURIComponent(t('ROUTES.CARGUIDE_FLOW'))}`,
  result: `/${carguideUrl}/${encodeURIComponent(t('ROUTES.CARGUIDE_RESULT'))}`
});

interface AddCarguideAnswerObject {
  tag: keyof CarGuideAnswers;
  index: number;
}

export type AddCarguideAnswer = (
  { tag, index }: AddCarguideAnswerObject,
  value: string
) => void;

export type CarGuideProps = RouteComponentProps;
const CarGuide = ({ route, location, history }: CarGuideProps) => {
  const { t } = useTranslation();

  const [isNavigating, setIsNavigating] = useState(false);
  const idToken = useSelector(idTokenSelector);
  const carguideUrl = encodeURIComponent(t('ROUTES.CARGUIDE'));
  const steps = getSteps(carguideUrl, t);
  const [seenIntro, setSeenIntro] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Partial<CarGuideAnswers>>();
  const [answersLoaded, setAnswersLoaded] = useState(false);
  const [answersChanged, setAnswersChanged] = useState(false);
  const [changingAnswer, setChangingAnswer] = useState(false);
  const [isAnswerSavingInMemberService, setIsAnswerSavingInMemberService] =
    useState(false);
  const [isAnswerSavingInLocalForage, setIsAnswerSavingInLocalForage] =
    useState(false);

  const { data: cmsData } = useCarguideCmsData();

  const [results, setResults] = useState<CarGuideResults['data']>({
    results: [],
    total: 0
  });
  const [resultRequestHandler, setResultRequestHandler] = useState(
    initialRequestHandler
  );

  const answersLength = answers ? Object.keys(answers).length : 0;

  const questions = useMemo(() => cmsData?.questions ?? [], [cmsData]);
  const questionsLoaded = questions.length > 0;
  const answeredAll = questionsLoaded && answersLength === questions.length;

  const resultsFetched = resultRequestHandler.isFetched;

  const getPathnameToRedirect = useCallback(() => {
    if (!answeredAll) {
      if (!seenIntro && answersLength === 0) {
        return steps.start;
      }

      if (answersLength > 0) {
        return steps.flow;
      }
    }

    if (answeredAll) {
      return steps.result;
    }
  }, [steps, seenIntro, answeredAll, answersLength]);

  const pathname = getPathnameToRedirect();
  const routeShouldChange =
    pathname && pathname !== location.pathname && !isNavigating;
  const push = history.push;

  useEffect(() => {
    const unlisten = history.listen(() => {
      setIsNavigating(false);
    });

    return () => {
      unlisten();
    };
  });

  useEffect(() => {
    if (questionsLoaded && answersLoaded && routeShouldChange) {
      setIsNavigating(true);
      pathname && push(pathname);
    }
  }, [answersLoaded, pathname, push, questionsLoaded, routeShouldChange]);

  const restart = useCallback(() => {
    localForageRemoveItem('carguide')
      .then(() => {
        setCurrentQuestion(0);
        setAnswers({});
        setResultRequestHandler(initialRequestHandler);
        setResults({ results: [], total: 0 });
        setSeenIntro(false);
      })
      .catch(localForageErrorHandler);
  }, []);

  const addAnswer = (
    { tag, index }: AddCarguideAnswerObject,
    value: string
  ) => {
    setIsAnswerSavingInMemberService(true);
    setIsAnswerSavingInLocalForage(true);
    const newAnswers = {
      ...answers,
      [tag]: value
    };
    saveMemberAnswers({ answers: newAnswers, idToken })
      .then(() => {
        setAnswers(newAnswers);
        setAnswersChanged(true);
        setIsAnswerSavingInMemberService(false);
      })
      .catch(requestErrorHandler);

    localForageSetItem('carguide', newAnswers)
      .then(() => {
        const nextQuestion =
          Number(index) + 1 > questions.length ? -1 : Number(index) + 1;
        setCurrentQuestion(nextQuestion);
        setIsAnswerSavingInLocalForage(false);
      })
      .catch(localForageErrorHandler);
  };

  const isPreviousQuestion = currentQuestion < answersLength;

  useEffect(() => {
    if (
      answeredAll &&
      (answersChanged ||
        (!resultRequestHandler.isLoading && !resultRequestHandler.isFetched))
    ) {
      setResultRequestHandler({
        ...initialRequestHandler,
        isLoading: true
      });
      setAnswersChanged(false);
      answers &&
        getCarguideResultsCall({ answers, limit: CarGuideResultsLimit })
          .then(({ data }: GetCarGuideResultsResponse) => {
            setResults(data?.carGuide ?? { results: [], total: 0 });
            setResultRequestHandler({
              ...initialRequestHandler,
              isFetched: true
            });
          })
          .catch((err: unknown) => {
            setResultRequestHandler({
              isLoading: false,
              isError: true,
              isFetched: true
            });
            requestErrorHandler(err);
          });
    }
  }, [
    answeredAll,
    answers,
    answersChanged,
    resultRequestHandler.isLoading,
    resultRequestHandler.isFetched
  ]);

  useEffect(() => {
    const noAnswers = answersLength === 0;
    const questionsLoaded = questions.length > 0;
    if (noAnswers && questionsLoaded) {
      localForageGetItem<CarGuideAnswers>('carguide')
        .then(answersFromForage => {
          if (answersFromForage && typeof answersFromForage === 'object') {
            const savedAnswerKeys = Object.keys(
              answersFromForage
            ) as Array<CarguideQuestionType>;
            const validSavedData = isValidSavedAnswers({
              savedAnswerKeys,
              currentQuestions: questions
            });

            if (!validSavedData) {
              restart();
              return;
            }

            setAnswers(answersFromForage);
            setCurrentQuestion(savedAnswerKeys.length);
          }
        })
        .catch(localForageErrorHandler)
        .finally(() => setAnswersLoaded(true));
    }
  }, [answersLength, questions, setAnswers, restart]);
  const question = questions[currentQuestion];

  useEffect(() => {
    if (answersChanged) {
      setChangingAnswer(true);
    }
    if (changingAnswer && resultRequestHandler.isFetched) {
      setChangingAnswer(false);
    }
  }, [
    answersChanged,
    setChangingAnswer,
    changingAnswer,
    resultRequestHandler.isFetched
  ]);

  return (
    <>
      <KvdHelperWrapper url={cmsData?.kvdHelperUrl ?? ''}>
        {questionsLoaded &&
        answersLoaded &&
        (!answeredAll || changingAnswer || (answeredAll && resultsFetched)) ? (
          <Wrapper>
            {renderRoutes(route?.routes, {
              carguideUrl,
              data: cmsData,
              addAnswer,
              answers,
              index: currentQuestion,
              questions,
              setCurrentQuestion,
              isPreviousQuestion,
              results: {
                requestHandler: resultRequestHandler,
                data: results
              },
              restart,
              steps,
              question,
              answer: answers && question && answers[question?.tag],
              questionsLength: questions.length,
              setSeenIntro,
              isAnswerSaving:
                isAnswerSavingInMemberService || isAnswerSavingInLocalForage
            })}
          </Wrapper>
        ) : (
          <StatusPageLoading />
        )}
      </KvdHelperWrapper>
    </>
  );
};

export default CarGuide;
